import { useState } from "react";
import { Navigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { adminApi, type AdminRole, type CreateAdminUserRequest } from "@/lib/adminApi";
import { adminAuth } from "@/lib/adminAuth";
import { isSuperAdmin } from "@/lib/roles";

export default function AdminUsers() {
  const qc = useQueryClient();
  const me = useQuery({ queryKey: ["admin-me"], queryFn: () => adminAuth.me(), retry: false });
  const users = useQuery({
    queryKey: ["admin-users"],
    queryFn: () => adminApi.listUsers(),
    enabled: isSuperAdmin(me.data),
  });

  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<Date | null>(null);
  const [form, setForm] = useState<CreateAdminUserRequest>({
    email: "",
    displayName: "",
    password: "",
    roles: ["Admin"],
  });

  // Reset-password modal state
  const [resetTarget, setResetTarget] = useState<{ id: string; email: string } | null>(null);
  const [resetPassword, setResetPassword] = useState("");
  const [resetConfirm, setResetConfirm] = useState("");
  const [resetError, setResetError] = useState<string | null>(null);
  const [resetDone, setResetDone] = useState<{ email: string } | null>(null);

  const resetPw = useMutation({
    mutationFn: () => adminApi.resetUserPassword(resetTarget!.id, resetPassword),
    onSuccess: () => {
      setResetDone({ email: resetTarget!.email });
      setResetTarget(null);
      setResetPassword("");
      setResetConfirm("");
      setResetError(null);
    },
    onError: (e: any) => setResetError(e?.response?.data?.detail ?? "Reset failed."),
  });

  const submitReset = (e: React.FormEvent) => {
    e.preventDefault();
    setResetError(null);
    if (resetPassword.length < 8) { setResetError("Password must be at least 8 characters."); return; }
    if (resetPassword !== resetConfirm) { setResetError("Passwords do not match."); return; }
    resetPw.mutate();
  };

  const create = useMutation({
    mutationFn: () => adminApi.createUser(form),
    onSuccess: () => {
      setError(null);
      setSavedAt(new Date());
      setForm({ email: "", displayName: "", password: "", roles: ["Admin"] });
      setCreating(false);
      qc.invalidateQueries({ queryKey: ["admin-users"] });
    },
    onError: (e: any) => setError(e?.response?.data?.detail ?? "Create failed."),
  });

  if (me.isLoading) return <Loading />;
  if (!isSuperAdmin(me.data)) return <Navigate to="/admin" replace />;

  return (
    <main className="mx-auto max-w-4xl p-6">
      <div className="mb-4 flex items-center justify-between">
        <h1 className="text-2xl font-bold text-brand-ink">Admin users</h1>
        <button className="btn-primary" onClick={() => setCreating(true)}>New user</button>
      </div>

      <div className="card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-brand-light text-brand-ink">
            <tr>
              {["Email", "Name", "Roles", "Active", "Created", ""].map((h) => (
                <th key={h} className="px-3 py-2 text-left font-semibold">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {users.data?.map((u) => (
              <tr key={u.id} className="border-t border-brand-ink/10">
                <td className="px-3 py-2">{u.email}</td>
                <td className="px-3 py-2">{u.displayName}</td>
                <td className="px-3 py-2">
                  <div className="flex flex-wrap gap-1">
                    {u.roles.map((r) => (
                      <span key={r} className={`rounded-pill px-2 py-0.5 text-xs
                        ${r === "SuperAdmin" ? "bg-brand text-white"
                          : r === "Admin" ? "bg-emerald-100 text-emerald-800"
                          : "bg-yellow-100 text-yellow-800"}`}>
                        {r}
                      </span>
                    ))}
                  </div>
                </td>
                <td className="px-3 py-2">{u.isActive ? "Yes" : "No"}</td>
                <td className="whitespace-nowrap px-3 py-2 text-xs text-brand-ink/70">
                  {new Date(u.createdAt).toLocaleString()}
                </td>
                <td className="whitespace-nowrap px-3 py-2 text-right text-sm">
                  {u.id === me.data?.id ? (
                    <span className="text-brand-ink/40" title="Use Change password in the header for your own account">
                      —
                    </span>
                  ) : (
                    <button
                      className="text-brand hover:underline"
                      onClick={() => {
                        setResetTarget({ id: u.id, email: u.email });
                        setResetDone(null);
                        setResetError(null);
                        setResetPassword(""); setResetConfirm("");
                      }}>
                      Reset password
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {users.data?.length === 0 && (
              <tr><td colSpan={6} className="px-3 py-6 text-center text-brand-ink/60">
                No users yet.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {savedAt && (
        <div className="mt-3 text-sm text-emerald-700">
          ✓ Created at {savedAt.toLocaleTimeString()}.
        </div>
      )}
      {resetDone && (
        <div className="mt-3 rounded-lg bg-emerald-50 p-3 text-sm text-emerald-800">
          ✓ Password reset for <b>{resetDone.email}</b>. Share the new password with the user
          out-of-band; they should change it on next sign-in.
        </div>
      )}

      {resetTarget && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4">
          <form className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl" onSubmit={submitReset}>
            <h3 className="text-lg font-bold text-brand-ink">Reset password</h3>
            <p className="mt-1 text-sm text-brand-ink/60">
              For <b>{resetTarget.email}</b>. The user's existing password will stop working immediately.
            </p>

            <label className="field-label mt-4">New password</label>
            <input className="field-input" type="password" required minLength={8}
              autoComplete="new-password" autoFocus
              value={resetPassword} onChange={(e) => setResetPassword(e.target.value)} />

            <label className="field-label mt-4">Confirm new password</label>
            <input className="field-input" type="password" required minLength={8}
              autoComplete="new-password"
              value={resetConfirm} onChange={(e) => setResetConfirm(e.target.value)} />

            <p className="mt-2 text-xs text-brand-ink/60">
              At least 8 characters. Share it with the user out-of-band; they should change it after next login.
            </p>

            {resetError && <div className="mt-3 text-sm text-brand" role="alert">{resetError}</div>}

            <div className="mt-6 flex justify-end gap-2">
              <button type="button" className="btn-secondary"
                onClick={() => { setResetTarget(null); setResetError(null); }}>
                Cancel
              </button>
              <button type="submit" className="btn-primary" disabled={resetPw.isPending}>
                {resetPw.isPending ? "Resetting…" : "Reset password"}
              </button>
            </div>
          </form>
        </div>
      )}

      {creating && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4">
          <form className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl"
            onSubmit={(e) => { e.preventDefault(); create.mutate(); }}>
            <h3 className="text-lg font-bold text-brand-ink">New admin user</h3>

            <label className="field-label mt-4">Email</label>
            <input className="field-input" type="email" required autoComplete="off"
              value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />

            <label className="field-label mt-4">Display name</label>
            <input className="field-input" required
              value={form.displayName} onChange={(e) => setForm({ ...form, displayName: e.target.value })} />

            <label className="field-label mt-4">Temporary password</label>
            <input className="field-input" type="password" required minLength={8} autoComplete="new-password"
              value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
            <p className="mt-1 text-xs text-brand-ink/60">
              At least 8 characters. Share it with the user out-of-band; they should change it after first login.
            </p>

            <label className="field-label mt-4">Role</label>
            <div className="mt-1 grid grid-cols-1 gap-2">
              <RoleOption
                value="Admin" current={form.roles} onChange={(rs) => setForm({ ...form, roles: rs })}
                caption="Full access. Can manage questions, quizzes, results, and audit log." />
              <RoleOption
                value="Instructor" current={form.roles} onChange={(rs) => setForm({ ...form, roles: rs })}
                caption="Cannot see or edit the question bank. Can create quizzes, attach categories, and view results." />
            </div>

            {error && <div className="mt-3 text-sm text-brand">{error}</div>}

            <div className="mt-6 flex justify-end gap-2">
              <button type="button" className="btn-secondary"
                onClick={() => { setCreating(false); setError(null); }}>
                Cancel
              </button>
              <button type="submit" className="btn-primary" disabled={create.isPending}>
                {create.isPending ? "Creating…" : "Create user"}
              </button>
            </div>
          </form>
        </div>
      )}
    </main>
  );
}

function RoleOption({
  value, current, onChange, caption,
}: {
  value: AdminRole;
  current: AdminRole[];
  onChange: (rs: AdminRole[]) => void;
  caption: string;
}) {
  const selected = current.includes(value);
  return (
    <label className={`flex cursor-pointer items-start gap-2 rounded-lg border px-3 py-2 transition
      ${selected ? "border-brand bg-brand-light" : "border-brand-ink/20 hover:bg-brand-light"}`}>
      <input type="radio" name="role" className="mt-0.5 accent-brand"
        checked={selected} onChange={() => onChange([value])} />
      <div>
        <div className="text-sm font-semibold text-brand-ink">{value}</div>
        <div className="text-xs text-brand-ink/60">{caption}</div>
      </div>
    </label>
  );
}

function Loading() {
  return <main className="grid min-h-[40vh] place-items-center text-brand-ink/60">Loading…</main>;
}
